/**
 * Firebase Authentication — Google and GitHub (blueprint §9.4).
 *
 * The web SDK's only job here is to obtain an ID token. Every authorisation
 * decision happens in the Go backend, which verifies that token server-side. The
 * client is never trusted with plan, seat or entitlement state, which is why this
 * file has no notion of any of them.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  GithubAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
  signInWithPopup,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from 'firebase/auth'

import { env } from './env'

let app: FirebaseApp | undefined
let authInstance: Auth | undefined

export function firebaseAuth(): Auth {
  if (!authInstance) {
    app = app ?? initializeApp(env.firebase)
    authInstance = getAuth(app)
    if (env.firebaseAuthEmulatorHost) {
      // E2E only: the emulator issues tokens the API verifies with
      // FIREBASE_AUTH_EMULATOR_HOST set. Unset everywhere else, so this never
      // runs against the real project.
      connectAuthEmulator(authInstance, `http://${env.firebaseAuthEmulatorHost}`, {
        disableWarnings: true,
      })
    }
    // Local persistence keeps a session across tab closes, which is what a person
    // expects from an account portal. The promise is intentionally not awaited:
    // persistence applies to subsequent sign-ins and blocking module load on it
    // would delay first paint for no benefit.
    void setPersistence(authInstance, browserLocalPersistence)
  }
  return authInstance
}

export type ProviderId = 'google' | 'github'

function providerFor(id: ProviderId) {
  if (id === 'github') {
    const provider = new GithubAuthProvider()
    // GitHub hides the email by default and the backend cannot deduplicate a
    // person across providers without one — which is what the one-account-per-person
    // rule depends on. Requesting the scope explicitly is the difference between a
    // working sign-up and a confusing failure.
    provider.addScope('user:email')
    return provider
  }
  const provider = new GoogleAuthProvider()
  // Always show the chooser: a developer with a personal and a work Google account
  // must not be silently signed in as whichever one the browser remembers.
  provider.setCustomParameters({ prompt: 'select_account' })
  return provider
}

export class SignInError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message)
    this.name = 'SignInError'
  }
}

export async function signInWith(id: ProviderId): Promise<User> {
  try {
    const result = await signInWithPopup(firebaseAuth(), providerFor(id))
    return result.user
  } catch (error) {
    throw new SignInError(describeSignInError(error), signInErrorCode(error))
  }
}

/**
 * Enterprise SSO sign-in. The provider id comes from the backend's discovery
 * endpoint ("saml.acme-okta" / "oidc.acme-entra") — Firebase Identity Platform
 * hosts the actual SAML/OIDC exchange, so this is one popup either way.
 */
export async function signInWithSSO(providerId: string): Promise<User> {
  const provider = providerId.startsWith('saml.')
    ? new SAMLAuthProvider(providerId)
    : new OAuthProvider(providerId)
  try {
    const result = await signInWithPopup(firebaseAuth(), provider)
    return result.user
  } catch (error) {
    throw new SignInError(describeSignInError(error), signInErrorCode(error))
  }
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(firebaseAuth())
}

/**
 * Returns a fresh ID token, or null when nobody is signed in.
 *
 * `getIdToken()` refreshes automatically when the cached token is within five
 * minutes of expiry, so callers can treat this as always-fresh and every API call
 * can go through it without tracking expiry itself.
 */
export async function currentIdToken(forceRefresh = false): Promise<string | null> {
  const user = firebaseAuth().currentUser
  if (!user) return null
  return user.getIdToken(forceRefresh)
}

function signInErrorCode(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return String((error as { code: unknown }).code)
  }
  return 'unknown'
}

function describeSignInError(error: unknown): string {
  switch (signInErrorCode(error)) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled.'
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.'
    case 'auth/account-exists-with-different-credential':
      // The provider-collision case from blueprint §9.4, explained rather than
      // dead-ended, because the person's account genuinely does exist.
      return 'An account already exists for this email address with a different provider. Sign in with the provider you used originally.'
    case 'auth/network-request-failed':
      return 'Could not reach the sign-in service. Check your connection and try again.'
    case 'auth/unauthorized-domain':
      return 'This domain is not authorised in the Firebase project. Add it under Authentication → Settings → Authorized domains.'
    default:
      return 'Sign-in failed. Please try again.'
  }
}
